import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AccountCard from '../../components/AccountCard';
import CardCategories from '../../components/CardCategories';
import CardSelect from './components/CardSelect';
import Button from '../../../../components/global/Button';
import useAccountCardConfigure from './useAccountCardConfigure';
import AccountCardDismissConfirm from './components/AccountCardDismissConfirm/AccountCardDismissConfirm';
import useAccountCardCheckout from './useAccountCardCheckout';
import AccountCardCheckoutSuccessAlert from './components/AccountCardCheckoutSuccessAlert';
import Angle from '../../../../components/global/Angle';
import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../constants';
import useIsNativeWebView from '../../../native-bridge/hooks/useIsWebView';
import {
  postNativeAppCardsConfiguratorComplete,
  postNativeAppCardsConfiguratorDismiss,
} from '../../../native-bridge/native-apps-interface';
import ContactSpreadsheetImportModal from '../../components/ContactSpreadsheetImport/modal';
import CardSideSelect from './components/CardSideSelect';
import { canSelectBackCardSideGroups } from './helpers';
import useVirtualPageView from '../../../../apps/pro/services/analytics/useVirtualPageView';
import AccountCardCheckoutPayment from './components/AccountCardCheckoutPayment/modal';
import AccountCardReview from './components/AccountCardReview';
import AccountCardAddressBookTable from './components/AccountCardAddressBookTable/modal';
import { mapRosterIdentitiesToCustomPrintRecipients } from '../../../../apps/pro/views/AccountAddressBook/components/AccountAddressBookTable/helpers';
import {
  getAccountCardElementsConfiguration,
  isCardElementEditable,
} from '../../components/AccountCard/helpers';
import useBasename from '../../../../components/routing/useBasename';
import { BASENAME } from '../../../../constants/basenames';
import CardElementAddressControl from '../../components/CardElementAddress/CardElementAddressControl/CardElementAddressControl';
import CardElementStickerControl from '../../components/CardElementSticker/CardElementStickerControl';
import AccountCardAddToCartSuccess from './components/AccountCardAddToCartSuccess/modal';
import DismissButton from '../../../../components/global/DismissButton';
import AccountCardStickerConfirm from './components/AccountCardStickerConfirm/AccountCardStickerConfirm';
import useOpenModal from '../../../../hooks/useOpenModal';
import { CHECKOUT_CART_MODAL } from '../../../checkout/components/CheckoutCart/modal';

const propTypes = {
  className: PropTypes.string,
  accountCardId: PropTypes.string,
  onExit: PropTypes.func,
  onAccountCardChange: PropTypes.func,
  presetCardElementConfigurationInputs: PropTypes.arrayOf(
    PropTypes.shape({
      cardElement: PropTypes.shape({
        type: PropTypes.string.isRequired,
      }).isRequired,
      cardElementConfiguration: PropTypes.object.isRequired,
    }),
  ),
  photosContext: PropTypes.shape({
    account_id: PropTypes.string,
    album_id: PropTypes.string,
    identity_id: PropTypes.string,
    roster_identity_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  defaultCardCategoryId: PropTypes.string,
  sharingEnabled: PropTypes.bool,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  hideHeader: PropTypes.bool,
  onAccountCardChanged: PropTypes.func,
  onReviewModeChanged: PropTypes.func,
  allowReviewing: PropTypes.bool,
  reviewProps: PropTypes.shape({
    onCheckout: PropTypes.func,
  }),
  onAddToCart: PropTypes.func,
  hasCartItems: PropTypes.bool,
  mappings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
};

const AccountCardConfigurator = ({
  className,
  onExit,
  accountCardId,
  onAccountCardChange,
  presetCardElementConfigurationInputs,
  photosContext,
  defaultCardCategoryId,
  sharingEnabled = true,
  title = 'Create and mail a personalized card in a few taps!',
  subtitle = '',
  hideHeader,
  onAccountCardChanged,
  onReviewModeChanged,
  allowReviewing,
  reviewProps,
  onAddToCart,
  hasCartItems,
  mappings,
}) => {
  const basename = useBasename();
  const openModal = useOpenModal();
  const hasSeenBackSideSelection = useRef(false);
  const isNativeWebView = useIsNativeWebView();
  const [selectedCardCategory, setSelectedCardCategory] = useState(
    defaultCardCategoryId ? { id: defaultCardCategoryId } : null,
  );
  const [canSelectCardDesign, setCanSelectCardDesign] = useState(true);
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [side, setSide] = useState(CARD_SIDE.FRONT);
  const [reviewing, setReviewing] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const [stickerAlertDismissed, setStickerAlertDismissed] = useState(false);

  // Ensure this effect is run *before* onAccountCardChange. This way,
  // review changes will not override accountCard update changes.
  useEffect(() => {
    onReviewModeChanged?.(reviewing);
  }, [reviewing]);

  const {
    onCardChange,
    onElementConfigurationChange,
    onAccountCardDelete,
    onConfigurationReset,
    accountCard,
    authenticated,
    cardElementLoading,
  } = useAccountCardConfigure({
    accountCardId,
    setSelectedCardCategory,
    onAccountCardChange,
    presetCardElementConfigurationInputs,
  });
  const [selectedRosterIdentityIds, setSelectedRosterIdentityIds] = useState(
    photosContext?.roster_identity_ids ?? [],
  );
  const [cardSelectModalOpen, setCardSelectModalOpen] = useState(
    !accountCard && !accountCardId,
  );
  const [
    backCardSideSelectModalOpen,
    setBackCardSideSelectModalOpen,
  ] = useState(false);

  const [
    recipientSelectionModalOpen,
    setRecipientSelectionModalOpen,
  ] = useState(false);
  const [stickerAlertOpen, setStickerAlertOpen] = useState(false);
  const [stickerModalOpen, setStickerModalOpen] = useState(false);

  const {
    pricing,
    loadingPricing,
    canCheckout,
    checkingOut,
    checkoutSuccess,
    onCheckout,
    onCheckoutReset,
    paymentSummaryOpen,
    setPaymentSummaryOpen,
    promoCode,
    setPromoCode,
    globalAccountCard,
  } = useAccountCardCheckout({
    accountCard,
  });

  const { onVirtualPageViewTrack } = useVirtualPageView({
    fireOnMount: false,
  });

  // Pop the back side selection modal open when the
  // user first turns the card.
  useEffect(() => {
    if (
      side === CARD_SIDE.BACK &&
      canSelectBackCardSideGroups(accountCard) &&
      !hasSeenBackSideSelection.current &&
      // Don't show the selector if this is a draft/sent
      !accountCardId
    ) {
      hasSeenBackSideSelection.current = true;
      setTimeout(() => {
        setBackCardSideSelectModalOpen(true);
      }, 300);
    }

    if (accountCard) {
      onVirtualPageViewTrack({
        pageViewTitle: `Create a card - Edit the ${side.toLowerCase()} side`,
        pageViewPath: `/side/${side}`,
      });
    }
  }, [side]);

  useEffect(() => {
    onAccountCardChanged?.({
      accountCard,
      globalAccountCard,
      setDismissModalOpen,
      selectedCardCategory,
      setSelectedCardCategory,
      setReviewing,
    });
  }, [selectedCardCategory, accountCard, globalAccountCard]);

  const classes = classNames({
    'account-card-configurator': true,
    'is-configuring': !!selectedCardCategory && !reviewing,
    'is-reviewing': reviewing,
    [className]: !!className,
  });

  const state = accountCard
    ? getAccountCardElementsConfiguration(accountCard, cardElementLoading)
    : null;
  const addressElement = (state ?? []).find(
    element => element.cardElement.type === CARD_ELEMENT_TYPE.ADDRESS,
  );
  const stickerElements = (state ?? []).filter(
    element => element.cardElement.type === CARD_ELEMENT_TYPE.STICKER,
  );
  // For empty stickers, we only care for them if we can actually edit them
  const emptyStickerElements = stickerElements.filter(
    element =>
      !element.cardElementConfiguration?.system_sticker &&
      isCardElementEditable(element),
  );
  const stickerElement = emptyStickerElements?.[0];

  useEffect(() => {
    //  Ensure that if account card exist, and addressElement is set,
    //  we do not try to show the modal to the user.
    const { cardElementConfiguration } = addressElement ?? {};

    //  If there's no addresses at all, reset reviewing
    if (
      cardElementConfiguration?.external_shipping_addresses?.total === 0 &&
      cardElementConfiguration?.roster_identity_linked_identity_addresses
        ?.total === 0
    ) {
      setReviewing(false);
    }
  }, [accountCard]);

  const resetConfigurator = () => {
    setAddToCartSuccess(false);
    setDismissModalOpen(false);
    setSelectedCardCategory(null);
    onConfigurationReset();
    onCheckoutReset();
    setReviewing(false);
  };

  return (
    <div className={classes}>
      {selectedCardCategory && !reviewing && (
        <Fragment>
          {!hideHeader && (
            <div className="account-card-configurator-header">
              <DismissButton
                onClick={() => {
                  if (accountCard) {
                    setDismissModalOpen(true);
                  } else {
                    if (isNativeWebView) {
                      postNativeAppCardsConfiguratorDismiss();
                      return;
                    }

                    setSelectedCardCategory(null);
                  }
                }}
              />
              <h4 className="account-card-configurator-title">
                Create your Card
              </h4>
            </div>
          )}

          {accountCard && (
            <AccountCard
              accountCard={accountCard}
              onElementConfigurationChange={onElementConfigurationChange}
              photosContext={
                selectedRosterIdentityIds.length > 0
                  ? { roster_identity_ids: selectedRosterIdentityIds }
                  : undefined
              }
              side={side}
              onSideChange={() => {
                setSide(prev =>
                  prev === CARD_SIDE.FRONT ? CARD_SIDE.BACK : CARD_SIDE.FRONT,
                );
              }}
              setSelectedRosterIdentityIds={setSelectedRosterIdentityIds}
              cardElementLoading={cardElementLoading}
              mappings={mappings}
              disablePhotoWatermarkOverlay
            />
          )}

          <div className="account-card-configurator-actions">
            {canSelectCardDesign && accountCard && side === CARD_SIDE.FRONT && (
              <Button
                btnOutline
                btnBlock
                onClick={() => {
                  setCardSelectModalOpen(true);
                }}
              >
                Change Template
              </Button>
            )}

            {side === CARD_SIDE.BACK &&
              canSelectBackCardSideGroups(accountCard) && (
                <Button
                  btnOutline
                  btnBlock
                  onClick={() => {
                    setBackCardSideSelectModalOpen(true);
                  }}
                >
                  Change card back
                </Button>
              )}

            {((canCheckout && pricing) || allowReviewing) && (
              <div className="account-card-send-button">
                <Button
                  btnBlock
                  data-testid="account-card-send-button"
                  onClick={event => {
                    if (isNativeWebView) {
                      event.preventDefault();
                      event.stopPropagation();

                      postNativeAppCardsConfiguratorComplete(accountCard.id);
                      return;
                    }

                    // Only show the Alert if all stickers all empty
                    if (
                      !stickerAlertDismissed &&
                      stickerElements.length > 0 &&
                      stickerElements.length === emptyStickerElements.length &&
                      !stickerElement?.cardElementConfiguration?.system_sticker
                    ) {
                      setStickerAlertOpen(true);
                    } else {
                      setReviewing(true);
                    }
                  }}
                >
                  I&apos;m all set, continue
                </Button>
              </div>
            )}
          </div>

          <CardSelect
            forceOpen={cardSelectModalOpen}
            cardCategoryId={selectedCardCategory.id}
            onClose={individualCard => {
              if (!accountCard && !individualCard) {
                if (isNativeWebView) {
                  postNativeAppCardsConfiguratorDismiss();
                  return;
                }

                setSelectedCardCategory(null);
              }

              setCanSelectCardDesign(!individualCard);
              setCardSelectModalOpen(false);
            }}
            onCardSelect={async input => {
              await onCardChange(input);
              setCardSelectModalOpen(false);
            }}
            hasAccountCard={!!accountCard}
          />

          {accountCard && (
            <CardSideSelect
              forceOpen={backCardSideSelectModalOpen}
              side={CARD_SIDE.BACK}
              title="Choose a card back"
              accountCard={accountCard}
              onCardSideSelect={selectedCardSide =>
                onCardChange({
                  back_card_side_id: selectedCardSide.id,
                })
              }
              onClose={() => setBackCardSideSelectModalOpen(false)}
            />
          )}
        </Fragment>
      )}

      {!selectedCardCategory && !reviewing && (
        <Fragment>
          {onExit && (
            <button className="account-card-button-back" onClick={onExit}>
              <span className="account-card-button-back-icon">
                <Angle direction="left" />
              </span>
              Back
            </button>
          )}

          {title && <h3 className="text-black text-center">{title}</h3>}
          {subtitle}
          <CardCategories
            onCategorySelect={cardCategory => {
              setSelectedCardCategory(cardCategory);
              setCardSelectModalOpen(true);

              if (side === CARD_SIDE.BACK) {
                setSide(CARD_SIDE.FRONT);
              }
            }}
          />
        </Fragment>
      )}

      {reviewing && (
        <AccountCardReview
          onAddRecipients={() => setRecipientSelectionModalOpen(true)}
          accountCard={accountCard}
          cardElementLoading={cardElementLoading}
          onEdit={() => setReviewing(false)}
          onCheckout={() => setPaymentSummaryOpen(true)}
          onAddToCart={
            onAddToCart
              ? () => {
                  onAddToCart({ accountCard, globalAccountCard, pricing });
                  if (!hasCartItems) {
                    setAddToCartSuccess(true);
                  }
                }
              : undefined
          }
          hasCartItems={hasCartItems}
          checkingOut={checkingOut}
          onElementConfigurationChange={onElementConfigurationChange}
          {...reviewProps}
        />
      )}

      <AccountCardDismissConfirm
        forceOpen={dismissModalOpen}
        onCancel={() => setDismissModalOpen(false)}
        onConfirm={
          authenticated
            ? () => {
                if (isNativeWebView) {
                  postNativeAppCardsConfiguratorDismiss();
                  return;
                }

                resetConfigurator();
              }
            : undefined
        }
        onDiscard={async () => {
          if (authenticated) {
            await onAccountCardDelete();

            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
              return;
            }

            resetConfigurator();
          } else {
            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
              return;
            }

            resetConfigurator();
          }
        }}
        message={
          authenticated ? (
            <p>You can save your card as a draft or discard your card.</p>
          ) : (
            <p>If you close this, you will lose your card&rsquo;s progress.</p>
          )
        }
      />

      <AccountCardStickerConfirm
        forceOpen={stickerAlertOpen}
        onConfirm={() => {
          setStickerAlertOpen(false);
          setStickerAlertDismissed(true);
          setStickerModalOpen(true);
          // Turn the card to the position of the sticker element.
          if (stickerElement?.side) {
            setSide(stickerElement.side);
          }
        }}
        onDismiss={() => {
          setStickerAlertOpen(false);
          setStickerAlertDismissed(true);
          setReviewing(true);
        }}
      />

      {accountCard && (
        <AccountCardAddressBookTable
          forceOpen={
            recipientSelectionModalOpen && basename !== BASENAME.SHUTTERBUG
          }
          onClose={() => setRecipientSelectionModalOpen(false)}
          addresses={
            addressElement?.cardElementConfiguration
              ?.roster_identity_linked_identity_addresses?.records ?? []
          }
          defaultFamilyName={
            !!addressElement?.cardElementConfiguration?.use_family_name
          }
          onAddRecipients={(selectedRosterIdentities, useFamilyName) => {
            const rosterIdentityLinkedIdentities = mapRosterIdentitiesToCustomPrintRecipients(
              selectedRosterIdentities,
            );
            // Set the selected roster identities
            setSelectedRosterIdentityIds(
              rosterIdentityLinkedIdentities.map(
                ({ rosterIdentityId }) => rosterIdentityId,
              ),
            );
            const addressesIds = rosterIdentityLinkedIdentities
              .map(({ address }) => address?.id)
              .filter(Boolean);

            if (addressElement) {
              onElementConfigurationChange(addressElement, {
                roster_identity_linked_identity_address_ids: addressesIds,
                use_family_name: !!useFamilyName,
              });
            }
            setRecipientSelectionModalOpen(false);
          }}
        />
      )}

      {accountCard && (
        <CardElementAddressControl
          forceOpen={
            recipientSelectionModalOpen && basename === BASENAME.SHUTTERBUG
          }
          onClose={() => setRecipientSelectionModalOpen(false)}
          addresses={
            addressElement?.cardElementConfiguration
              ?.external_shipping_addresses?.records ?? []
          }
          defaultFamilyName={
            !!addressElement?.cardElementConfiguration?.use_family_name
          }
          accountCard={accountCard}
          onChange={input => {
            if (addressElement) {
              onElementConfigurationChange(addressElement, {
                external_shipping_address_ids: input.addresses.map(
                  ({ id }) => id,
                ),
                use_family_name: !!input.familyName,
              });
            }
            setRecipientSelectionModalOpen(false);
          }}
        />
      )}

      {accountCard && (
        <CardElementStickerControl
          forceOpen={stickerModalOpen}
          onClose={() => {
            setStickerModalOpen(false);
          }}
          onChange={newSystemSticker => {
            if (stickerElement) {
              onElementConfigurationChange(stickerElement, {
                system_sticker_id: newSystemSticker.id,
              });
            }
          }}
        />
      )}

      <AccountCardCheckoutPayment
        forceOpen={paymentSummaryOpen}
        onClose={() => setPaymentSummaryOpen(false)}
        pricing={pricing}
        onCheckout={onCheckout}
        loadingPricing={loadingPricing}
        submitting={checkingOut}
        accountCard={accountCard}
        onPromoCodeSubmit={value => setPromoCode(value)}
        defaultPromoCode={promoCode}
      />

      <AccountCardCheckoutSuccessAlert
        forceOpen={checkoutSuccess}
        onComplete={() => {
          resetConfigurator();
        }}
        sharingEnabled={sharingEnabled}
      />

      <AccountCardAddToCartSuccess
        forceOpen={addToCartSuccess}
        onCheckout={() => {
          setAddToCartSuccess(false);
          openModal(CHECKOUT_CART_MODAL);
        }}
        onMakeAnother={() => {
          resetConfigurator();
        }}
        onClose={() => {
          resetConfigurator();
        }}
      />

      <ContactSpreadsheetImportModal />
    </div>
  );
};

AccountCardConfigurator.propTypes = propTypes;

export default AccountCardConfigurator;
